import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";
import { ApiError } from "./errors.js";

/**
 * Auth strategy
 * -------------
 * The SPA holds the access token in memory only (never localStorage). The
 * refresh token lives in the HttpOnly `eos_refresh` cookie set by the BE
 * on `/auth/login` (TRD-06 §6.x). On 401 the client transparently calls
 * `/auth/refresh` (which replays the cookie automatically) and retries
 * the original request once. If refresh also 401s, the in-memory token
 * is cleared and the call site is left to redirect to `/login`.
 *
 * `withCredentials: true` is mandatory — without it the browser will not
 * send the refresh cookie on `/auth/refresh` and the user appears logged
 * out on every reload.
 */

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export interface CreateApiClientOptions {
  baseURL: string;
  /** Called when the refresh chain ultimately fails (e.g. cookie expired). */
  onUnauthorized?: () => void;
  /**
   * Lazily resolves the active tenant slug — called on every request so
   * the value can change at runtime (e.g. when the user navigates to a
   * different subdomain in a single SPA reload, or for tests). Returning
   * `null` means "do not send the header"; the BE will then either reject
   * the request or fall back to a body-level tenant_id (legacy path).
   */
  tenantSlugProvider?: () => string | null;
  /** Override for tests. */
  axios?: AxiosInstance;
}

export interface ApiClient {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  /** Set the in-memory access token (after login or refresh). */
  setAccessToken(token: string | null): void;
  /** Read the current in-memory access token (or null). */
  getAccessToken(): string | null;
  /**
   * Phase SC-22 — `false` puts the client in bearer-only mode: requests
   * go out without cookies and a 401 never triggers `/auth/refresh`. A
   * support-session tab uses this so it cannot replay (or get the BE to
   * clear) the super-admin's own `eos_refresh` cookie.
   */
  setCookieAuth(enabled: boolean): void;
  /** Replace the handler called when auth ultimately fails. */
  setUnauthorizedHandler(handler: (() => void) | null): void;
  /** Underlying axios instance for cases that need full control. */
  raw: AxiosInstance;
}

export function createApiClient(opts: CreateApiClientOptions): ApiClient {
  const instance = opts.axios ?? axios.create({ baseURL: opts.baseURL, withCredentials: true });
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
  let cookieAuth = true;

  // Attach the in-memory access token + tenant slug to every outgoing
  // request. Tenant slug is resolved lazily (per request, not per client)
  // so an SPA can re-host on a different subdomain without re-instantiating
  // the client.
  instance.interceptors.request.use((config) => {
    config.headers = config.headers ?? {};
    if (!cookieAuth) config.withCredentials = false;
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    const slug = opts.tenantSlugProvider?.() ?? null;
    if (slug) {
      config.headers["X-Tenant-Slug"] = slug;
    }
    return config;
  });

  // Single-flight refresh: if multiple requests fail concurrently with 401,
  // they all wait on the same /auth/refresh promise.
  let refreshInFlight: Promise<string | null> | null = null;
  const refresh = async (): Promise<string | null> => {
    if (!refreshInFlight) {
      refreshInFlight = (async () => {
        try {
          const resp = await instance.post<{ access_token: string }>("/auth/refresh", null, {
            _isRetry: true,
          } as AxiosRequestConfig & { _isRetry: boolean });
          accessToken = resp.data.access_token;
          return accessToken;
        } catch {
          accessToken = null;
          if (onUnauthorized) onUnauthorized();
          return null;
        } finally {
          refreshInFlight = null;
        }
      })();
    }
    return refreshInFlight;
  };

  instance.interceptors.response.use(
    (r) => r,
    async (err: AxiosError) => {
      const original = err.config as (AxiosRequestConfig & { _isRetry?: boolean }) | undefined;
      if (err.response?.status === 401 && original && !original._isRetry && !cookieAuth) {
        accessToken = null;
        if (onUnauthorized) onUnauthorized();
        return Promise.reject(unwrap(err));
      }
      if (err.response?.status === 401 && original && !original._isRetry) {
        // Don't recurse on the refresh endpoint itself.
        if ((original.url ?? "").endsWith("/auth/refresh")) {
          return Promise.reject(unwrap(err));
        }
        const fresh = await refresh();
        if (fresh) {
          original._isRetry = true;
          return instance.request(original);
        }
      }
      return Promise.reject(unwrap(err));
    },
  );

  const unwrap = (err: AxiosError): ApiError => {
    if (err.response) {
      return ApiError.fromResponse(err.response.status, err.response.data, err.message);
    }
    return new ApiError(err.message || "Network error", {
      status: 0,
      code: "NETWORK",
      cause: err,
    });
  };

  return {
    raw: instance,
    setAccessToken(token) {
      accessToken = token;
    },
    getAccessToken() {
      return accessToken;
    },
    setCookieAuth(enabled) {
      cookieAuth = enabled;
    },
    setUnauthorizedHandler(handler) {
      onUnauthorized = handler;
    },
    async get(url, config) {
      const r = await instance.get(url, config);
      return r.data;
    },
    async post(url, data, config) {
      const r = await instance.post(url, data, config);
      return r.data;
    },
    async put(url, data, config) {
      const r = await instance.put(url, data, config);
      return r.data;
    },
    async patch(url, data, config) {
      const r = await instance.patch(url, data, config);
      return r.data;
    },
    async delete(url, config) {
      const r = await instance.delete(url, config);
      return r.data;
    },
  };
}
